import type { IInitializeProductGroupVariant } from '../hooks/useInitializeProductGroupVariant';
import type { IInitializeProductVariant } from '../hooks/useInitializeCampaignProduct';
import type { IInitializeVariantProductVariant } from '~/modules/campaign-product';
import type { IBaseVariant } from '~/modules/campaign/types';
import type { IProduct } from '~/modules/product';
import type { ReactNode } from 'react';

import { useInitializeProductGroupVariant } from '../hooks/useInitializeProductGroupVariant';
import { useInitializeProductVariant } from '../hooks/useInitializeCampaignProduct';
import { useInitializeVariantProductVariant } from '~/modules/campaign-product';
import { createContext, useContext, useMemo } from 'react';

export interface IProductVariantContext
  extends
    IInitializeProductVariant,
    IInitializeProductGroupVariant,
    IInitializeVariantProductVariant {
  productId?: IProduct['id'];
  variants?: IBaseVariant[];
}
export interface IProductVariantProvider {
  children: ReactNode;
  productId?: IProduct['id'];
  variants?: IBaseVariant[];
}

export const ProductVariantContext = createContext<IProductVariantContext | undefined>(undefined);

export const ProductVariantProvider: React.FC<IProductVariantProvider> = ({
  children,
  productId,
  variants,
}) => {
  const initializeProductVariant = useInitializeProductVariant({ productId: productId ?? '' });
  const initializeProductVariantGroup = useInitializeProductGroupVariant(initializeProductVariant);
  const initializeVariantProductVariant = useInitializeVariantProductVariant(variants);

  const productWithVariantsGroup = initializeProductVariantGroup.productWithVariantsGroup;

  const isLoading = initializeVariantProductVariant.isLoading;

  const contextValue = useMemo(
    () => ({
      productId,
      variants,
      ...initializeProductVariant,
      ...initializeProductVariantGroup,
      ...initializeVariantProductVariant,
    }),
    [
      productId,
      variants,
      initializeProductVariant,
      initializeProductVariantGroup,
      initializeVariantProductVariant,
    ],
  );

  return (
    <ProductVariantContext.Provider value={contextValue}>
      {isLoading || !productWithVariantsGroup?.id ? null : children}
    </ProductVariantContext.Provider>
  );
};

export const useProductVariantContext = () => {
  const context = useContext(ProductVariantContext);
  if (!context) {
    throw new Error('useProductVariantContext must be used within a ProductVariantProvider');
  }
  return context;
};
